import React from 'react';
import { cyan, semantic, chartColors } from '../styles/color-tokens';

const SWATCH_WIDTH = 140;
const SWATCH_HEIGHT = 48;

function Swatch({ hex, name, label, size = 'md' }) {
  const h = size === 'lg' ? 64 : SWATCH_HEIGHT;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 14px',
      background: '#fff',
      borderRadius: 8,
      border: '1px solid #e9ecef',
    }}>
      <div style={{
        width: SWATCH_WIDTH,
        height: h,
        borderRadius: 6,
        background: hex,
        border: hex === '#fff' ? '1px solid #dee2e6' : 'none',
        flexShrink: 0,
      }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#32325d' }}>
          {name}
        </div>
        {label && (
          <div style={{ fontSize: 11, color: '#8898aa', fontFamily: 'monospace', marginTop: 1 }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, desc, children }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{
        fontSize: 14,
        fontWeight: 700,
        color: '#32325d',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 4,
      }}>{title}</h3>
      {desc && (
        <p style={{ fontSize: 13, color: '#8898aa', marginBottom: 12 }}>{desc}</p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

export default function ColorTokens() {
  const cyanEntries = Object.entries(cyan);
  const semanticEntries = Object.entries(semantic);

  return (
    <div>
      {/* Page Header */}
      <div className="dash-header">
        <div className="dash-header-bg" />
        <div className="dash-header-content" style={{ paddingBottom: 80 }}>
          <div className="dash-breadcrumb">
            <span>Referensi</span>
            <span className="dash-breadcrumb-sep">/</span>
            <span className="dash-breadcrumb-current">Color Tokens</span>
          </div>
          <h2 className="dash-title">Palet Warna Cyan</h2>
          <p className="dash-subtitle">
            Color tokens reusable di CSS <code style={{ background:'rgba(255,255,255,0.15)', padding:'2px 6px', borderRadius:4 }}>var(--cyan-*)</code>{' '}
            dan JS <code style={{ background:'rgba(255,255,255,0.15)', padding:'2px 6px', borderRadius:4 }}>import {'{ cyan }'} from '../styles/color-tokens'</code>
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="dash-content" style={{ marginTop: -60 }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', padding: 28 }}>

          {/* 1. Cyan Spectrum */}
          <Section title="1. Cyan Spectrum" desc="Dari terang (#e6fafc) ke gelap (#0a3845). var(--cyan-{n})">
            {cyanEntries.map(([name, hex]) => (
              <Swatch key={name} hex={hex} name={`cyan-${name}`} label={`${hex}  /* var(--cyan-${name}) */`} />
            ))}
          </Section>

          {/* 2. Semantic Colors */}
          <Section title="2. Semantic Colors" desc="Success, Warning, Danger — untuk status, badges, alert">
            {semanticEntries.map(([name, hex]) => (
              <Swatch key={name} hex={hex} name={name} label={`${hex}  /* var(--${name}) */`} size="lg" />
            ))}
          </Section>

          {/* 3. Chart Palette */}
          <Section title="3. Chart Palette" desc="Colors array untuk Recharts / pie charts">
            {chartColors.map((hex, i) => (
              <Swatch key={i} hex={hex} name={`COLORS[${i}]`} label={hex} />
            ))}
          </Section>

          {/* 4. Gradient Previews */}
          <Section title="4. Gradients" desc="Reusable gradient backgrounds">
            <div style={{
              width: 280,
              height: 48,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #14b8c4 0%, #0d7a8a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: 0.5,
            }}>
              var(--accent-gradient)
            </div>
            <div style={{
              width: 280,
              height: 48,
              borderRadius: 8,
              background: 'linear-gradient(90deg, #11cdef, #0d7a8a)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: 0.5,
            }}>
              var(--accent-gradient-h)
            </div>
            <div style={{
              width: 280,
              height: 48,
              borderRadius: 8,
              background: 'linear-gradient(180deg, #166072 0%, #0a3845 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: 0.5,
            }}>
              var(--sidebar-gradient)
            </div>
          </Section>

          {/* 5. Usage Guide */}
          <Section title="5. Usage Guide">
            <div style={{ fontSize: 13, color: '#525f7f', lineHeight: 1.8, fontFamily: 'monospace' }}>
              <div><span style={{ color: '#8898aa' }}>{'//'} CSS — pakai var(--cyan-*)</span></div>
              <div>.my-element {'{'} color: <span style={{ color: '#11cdef' }}>var(--cyan-400)</span>; background: <span style={{ color: '#11cdef' }}>var(--cyan-50)</span>; {'}'}</div>
              <br />
              <div><span style={{ color: '#8898aa' }}>{'//'} JS — import dari color-tokens.js</span></div>
              <div>{'import { cyan, chartColors, tintStyle } from \'../styles/color-tokens\';'}</div>
              <br />
              <div><span style={{ color: '#8898aa' }}>{'//'} Inline style icon background</span></div>
              <div>{'<div style={tintStyle(cyan[400])}><Icon /></div>'}</div>
              <br />
              <div><span style={{ color: '#8898aa' }}>{'//'} Chart colors</span></div>
              <div>{'const COLORS = chartColors;'}</div>
              <div>{'<Bar dataKey="count" fill={cyan[500]} />'}</div>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
